import { useEffect, useRef } from 'react';

/**
 * Hook to track time spent on a page and send it to the backend.
 * Uses visibilitychange + beforeunload + periodic flush to reliably capture time.
 * 
 * @param {string} pageUrl - Page identifier like 'learn', 'practice-reading', etc.
 * @param {object} user - User object (must have email)
 */
const usePageTimeTracker = (pageUrl, user) => {
  const startTimeRef = useRef(Date.now());
  const accumulatedRef = useRef(0);
  const flushedRef = useRef(false);

  useEffect(() => {
    if (!pageUrl || !user?.email) return;

    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';
    startTimeRef.current = Date.now();
    accumulatedRef.current = 0;
    flushedRef.current = false;

    const getSeconds = () => {
      const now = Date.now();
      const elapsed = Math.round((now - startTimeRef.current) / 1000) + accumulatedRef.current;
      return elapsed;
    };

    const sendTime = (seconds) => {
      if (seconds < 2 || flushedRef.current) return; // ignore trivial visits
      flushedRef.current = true;
      const payload = JSON.stringify({
        enterprise_id: user.email,
        page_url: pageUrl,
        time_spent: seconds,
      });
      // sendBeacon is reliable even on page unload
      const sent = navigator.sendBeacon(
        `${apiBase}/page-time`,
        new Blob([payload], { type: 'application/json' })
      );
      if (!sent) {
        // fallback fetch (fire-and-forget)
        fetch(`${apiBase}/page-time`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden — accumulate time so far
        accumulatedRef.current += Math.round((Date.now() - startTimeRef.current) / 1000);
      } else {
        // Page visible again — reset start
        startTimeRef.current = Date.now();
        flushedRef.current = false;
      }
    };

    const handleBeforeUnload = () => {
      sendTime(getSeconds());
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup: send time when component unmounts (navigation to another page)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      sendTime(getSeconds());
    };
  }, [pageUrl, user]);
};

export default usePageTimeTracker;
